/**
 * API types for Edge Functions
 * These types match the backend types exactly
 */

// ============================================================================
// Enums
// ============================================================================

export type ConversationStatus = 'open' | 'pending' | 'resolved' | 'closed';
export type MessageSenderType = 'customer' | 'operator' | 'system';

// ============================================================================
// Pagination
// ============================================================================

export interface PaginationInfo {
  next_cursor: string | null;
  has_more: boolean;
}

// ============================================================================
// List Conversations API
// ============================================================================

export interface ListConversationsParams {
  status?: ConversationStatus[];
  assignedTo?: 'me' | 'unassigned' | string;
  limit?: number;
  cursor?: string;
  since?: string;
}

export interface ConversationSummary {
  id: string;
  user_id: string;
  assigned_operator_id: string | null;
  status: ConversationStatus;
  subject: string | null;
  message_count: number;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListConversationsResponse {
  conversations: ConversationSummary[];
  pagination: PaginationInfo;
}

// ============================================================================
// Get Messages API
// ============================================================================

export interface GetMessagesParams {
  conversationId: string;
  limit?: number;
  cursor?: string;
  since?: string;
}

export interface MessageSummary {
  id: string;
  sender_type: MessageSenderType;
  sender_operator_id: string | null;
  sanitized_text: string;
  created_at: string;
  attachments?: AttachmentSummary[];
}

export interface GetMessagesResponse {
  messages: MessageSummary[];
  pagination: PaginationInfo;
  conversation: {
    id: string;
    status: ConversationStatus;
    assigned_operator_id: string | null;
  };
}

// ============================================================================
// Send Message API
// ============================================================================

export interface SendMessageRequest {
  conversation_id: string;
  text: string;
  idempotency_key: string;
}

export interface SendMessageResponse {
  message_id: string;
  conversation_id: string;
  created_at: string;
}

// ============================================================================
// Assign Conversation API
// ============================================================================

export interface AssignConversationRequest {
  conversation_id: string;
  operator_id: string | null;
}

export interface AssignConversationResponse {
  conversation_id: string;
  assigned_operator_id: string | null;
  assigned_at: string;
}

// ============================================================================
// Attachments API
// ============================================================================

export interface AttachmentSummary {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_path: string;
  download_url?: string;
}

export interface InitAttachmentRequest {
  conversation_id: string;
  message_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
}

export interface InitAttachmentResponse {
  upload_url: string;
  upload_token: string;
  storage_path: string;
  attachment_id: string;
  expires_at: string;
}

export interface FinalizeAttachmentRequest {
  attachment_id: string;
}

export interface FinalizeAttachmentResponse {
  attachment_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  finalized_at: string;
}

/**
 * Allowed MIME types for attachments (matches backend validation)
 */
export const ALLOWED_ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
] as const;

export type AllowedAttachmentType = (typeof ALLOWED_ATTACHMENT_TYPES)[number];

/**
 * Maximum attachment file size in bytes (50MB)
 */
export const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024;

// ============================================================================
// Error Types
// ============================================================================

export interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    request_id: string;
  };
}

export class ApiClientError extends Error {
  constructor(
    public code: string,
    message: string,
    public requestId: string,
    public status: number
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}
